<script setup lang="ts">
import { computed, ref, Ref } from 'vue';
import { useClickOutside } from '@/composables/clickOutside';
import { useColor } from '@/composables/color';
import ColorSelector from '../ColorSelector.vue';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';

const props = withDefaults(
    defineProps<{
        value?: string;
        title?: string;
        disabled?: boolean;
    }>(),
    { value: '#000000' }
);

const emit = defineEmits<{
    (e: 'input', value: string): void;
}>();

const { getColorFromString, hexToHexa } = useColor();

const color = computed<ColorObject>(() => {
    try {
        const parsed = getColorFromString(props.value);
        return { hex: parsed.hex, alpha: parsed.a, cmyk: '' };
    } catch {
        return { hex: '#000000', alpha: 1, cmyk: '' };
    }
});

const pickerRef: Ref<HTMLElement | undefined> = ref();

const { clickOutsideActive } = useClickOutside(
    pickerRef,
    () => {
        clickOutsideActive.value = false;
    },
    false
);

const togglePicker = () => {
    if (props.disabled) return;
    clickOutsideActive.value = !clickOutsideActive.value;
};

const onColorSelected = (selected: FontColorOption) => {
    const { hex, alpha } = selected as ColorObject;
    emit('input', alpha < 1 ? hexToHexa(hex, alpha) : hex);
    clickOutsideActive.value = false;
};
</script>

<template>
    <section class="calendar-color-field">
        <calendar-field-title v-if="title">{{ title }}</calendar-field-title>
        <div class="color-row rounded" :class="{ disabled }" @click="togglePicker">
            <span class="color-preview" :style="{ backgroundColor: color.hex, opacity: color.alpha }" />
            <span class="flex-fill">{{ color.hex.toUpperCase() }}</span>
            <font-awesome-icon :icon="['far', 'pen']" />
        </div>
        <color-selector
            ref="pickerRef"
            :open="clickOutsideActive"
            :color="color"
            @cancel="togglePicker"
            @color-selected="onColorSelected"
        />
    </section>
</template>

<style scoped lang="scss">
.calendar-color-field {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    .color-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem;
        cursor: pointer;
        background-color: #ebe8f7;

        &.disabled {
            cursor: not-allowed;
            opacity: 0.6;
        }

        .color-preview {
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 0.125rem;
            border: 1px solid #e6e6e6;
        }
    }
}
</style>
